/**
 * Payment-Link Link-Tester — Code-Snippets (Spec 501 Phase 6; Spec 503 Phase 6).
 *
 * Vollständige, korrekte Signier-Bodies für die vier Devportal-Sprachen. Der
 * Algorithmus folgt Handover §2/§3 exakt: alle Nicht-`sig`-Parameter, DEKODIERTE
 * Werte, byte-sortiert nach ASCII-Key, `key=value\n`-Zeilen INKL. Trailing-`\n`,
 * HMAC-SHA256 mit dem Salt ALS TEXT (kein Hex-Decode), Hex-lowercase.
 *
 * Sicherheits-Invariante (Spec §Scope 5, Handover §3): die AUSFÜHRBARE
 * Salt-Zuweisung trägt IMMER den `<SALT>`-Platzhalter, nie einen echten Salt (der
 * `SnippetContext` enthält bewusst KEIN Salt-Feld). Der offizielle Demo-Salt darf
 * ausschließlich in einem KOMMENTAR (Testvektor) erscheinen — nie als ausführbarer
 * Eingabewert (Snippets landen in Repos/Chats).
 */
import type { LinkParam } from './format-definition';

export const SNIPPET_LANGUAGES = [
  'php',
  'typescript',
  'nodejs',
  'python',
] as const;
export type SnippetLanguage = (typeof SNIPPET_LANGUAGES)[number];

/** Der einzige Salt-Wert, der je in ein Snippet gelangt. */
export const SALT_PLACEHOLDER = '<SALT>';

/** Kontext für Snippets — bewusst OHNE Salt (strukturelle Leak-Sperre). */
export interface SnippetContext {
  baseUrl: string;
  params: LinkParam[];
  signatureParamName: string;
}

/**
 * Übersetzbare Kommentar-Prosa (Integrations-Review M4) — der Aufrufer liefert sie
 * per i18n, damit EN-Nutzer keine gemischtsprachigen Snippets bekommen. Technische
 * Werte (Salt-/Sig-Hex, Parameter-Namen, Code) bleiben sprachneutral.
 */
export interface SnippetLabels {
  /** Titel-Prosa, z. B. „Payment-Link signieren (c=1)". */
  title: string;
  /** Testvektor-Label, z. B. „Testvektor:". */
  testVector: string;
  /** Salt-Kommentar, z. B. „Salt ALS TEXT — NICHT hex-dekodieren". */
  saltAsText: string;
  /** Sortier-/Basis-Kommentar (Byte-Sort + Trailing-\\n). */
  byteSort: string;
}

/** Escape für ein einfach-quotiertes Literal (PHP/JS): `\` und `'`. */
function esc1(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}
/** Escape für ein doppelt-quotiertes Literal (Python): `\` und `"`. */
function esc2(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Testvektor-Kommentar je Kommentar-Präfix (Demo-Salt NUR hier; Werte neutral). */
function testVectorComment(c: string, labels: SnippetLabels): string[] {
  return [
    `${c} ${labels.testVector} Salt "4a6f686e" (Demo) + svc=SVC_123, cbOk=https://ok.com,`,
    `${c}   m=gameloft, c=1  =>  sig =`,
    `${c}   136a727ed8711274606741171601fabea23d35b70d0db586fa0c4a47211f3eb7`,
  ];
}

/** Standard-Signatur-Parameter (query-append). */
const DEFAULT_SIG_PARAM = 'sig';

function phpSnippet(ctx: SnippetContext, labels: SnippetLabels): string {
  const sig = ctx.signatureParamName || DEFAULT_SIG_PARAM;
  const entries = ctx.params
    .map((p) => `  '${esc1(p.key)}' => '${esc1(p.value)}',`)
    .join('\n');
  return [
    '<?php',
    `// ${labels.title} — PHP`,
    ...testVectorComment('//', labels),
    `$salt = '${SALT_PLACEHOLDER}'; // ${labels.saltAsText}`,
    '$params = [',
    entries,
    '];',
    `ksort($params, SORT_STRING); // ${labels.byteSort}`,
    "$basis = '';",
    'foreach ($params as $k => $v) { $basis .= $k . \'=\' . $v . "\\n"; }',
    "$sig = hash_hmac('sha256', $basis, $salt); // lowercase hex",
    `$query = http_build_query($params) . '&${sig}=' . $sig;`,
    `$url = '${esc1(ctx.baseUrl)}?' . $query;`,
  ].join('\n');
}

function tsLikeSnippet(
  ctx: SnippetContext,
  labels: SnippetLabels,
  langName: string,
  typed: boolean,
): string {
  const sig = ctx.signatureParamName || DEFAULT_SIG_PARAM;
  const entries = ctx.params
    .map((p) => `  '${esc1(p.key)}': '${esc1(p.value)}',`)
    .join('\n');
  return [
    `// ${labels.title} — ${langName}`,
    ...testVectorComment('//', labels),
    "import { createHmac } from 'node:crypto';",
    `const salt = '${SALT_PLACEHOLDER}'; // ${labels.saltAsText}`,
    // Nur TypeScript trägt die Typ-Annotation; Node.js ist reines JavaScript.
    typed ? 'const params: Record<string, string> = {' : 'const params = {',
    entries,
    '};',
    `// ${labels.byteSort}`,
    'const basis = Object.keys(params)',
    '  .sort()',
    '  .map((k) => `${k}=${params[k]}\\n`)',
    "  .join('');",
    "const sig = createHmac('sha256', salt).update(basis).digest('hex');",
    'const query = Object.entries(params)',
    '  .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)',
    "  .join('&');",
    `const url = \`${esc1(ctx.baseUrl)}?\${query}&${sig}=\${sig}\`;`,
  ].join('\n');
}

function pythonSnippet(ctx: SnippetContext, labels: SnippetLabels): string {
  const sig = ctx.signatureParamName || DEFAULT_SIG_PARAM;
  const entries = ctx.params
    .map((p) => `    "${esc2(p.key)}": "${esc2(p.value)}",`)
    .join('\n');
  return [
    `# ${labels.title} — Python`,
    ...testVectorComment('#', labels),
    'import hmac, hashlib',
    'from urllib.parse import urlencode',
    `salt = "${SALT_PLACEHOLDER}"  # ${labels.saltAsText}`,
    'params = {',
    entries,
    '}',
    `# ${labels.byteSort}`,
    'basis = "".join(f"{k}={params[k]}\\n" for k in sorted(params))',
    'sig = hmac.new(salt.encode(), basis.encode(), hashlib.sha256).hexdigest()',
    `url = f"${esc2(ctx.baseUrl)}?{urlencode(params)}&${sig}={sig}"`,
  ].join('\n');
}

/**
 * Baut das Snippet einer Sprache. Enthält garantiert `<SALT>` in der ausführbaren
 * Zuweisung; der Demo-Salt erscheint nur im Testvektor-Kommentar. Kommentar-Prosa
 * kommt übersetzt über `labels` (M4).
 */
export function buildSnippet(
  lang: SnippetLanguage,
  ctx: SnippetContext,
  labels: SnippetLabels,
): string {
  switch (lang) {
    case 'php':
      return phpSnippet(ctx, labels);
    case 'typescript':
      return tsLikeSnippet(ctx, labels, 'TypeScript', true);
    case 'nodejs':
      return tsLikeSnippet(ctx, labels, 'Node.js', false);
    case 'python':
      return pythonSnippet(ctx, labels);
  }
}
